import React, { useEffect, useId, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, UserPlus, Users } from 'lucide-react';
import {
  Column, DANGER_GHOST_BTN, DataTable, DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, KeyValue, ListPage, ListToolbar,
  Notice, PRIMARY_BTN, SELECT_CLS, StatusChip, matchesSearch,
} from '../shared/listPage';
import { SheetField } from '../settings/sheet';
import { OrgMember, OrgStore } from './orgTypes';
import { TFn, roleLabel, storeLabel } from './orgFormat';
import OrgConfirm from './OrgConfirm';

/**
 * «Μέλη» των κεντρικών στη διάταξη «Πίνακας» (14/10/2026): πίνακας μελών + συρτάρι. ΙΔΙΕΣ κλήσεις με πριν:
 * ο ρόλος αλλάζει αμέσως (PUT …/members/:id { role }), η πρόσβαση σε καταστήματα με «Αποθήκευση»
 * (PUT { role, store_ids }, κενό = όλα), αφαίρεση με επιβεβαίωση. «Προσθήκη μέλους» (15/10/2026): «Νέος χρήστης»
 * (όνομα, email, αρχικός κωδικός → POST …/members/new) ή «Υπάρχων χρήστης» με org user id, όπως πριν.
 * Μόνο ιδιοκτήτης/διαχειριστής αλλάζει κάτι· οι υπόλοιποι βλέπουν τον πίνακα και τα στοιχεία.
 *
 * Το ίδιο και στο πάνελ support → «Αλυσίδες» (25/10/2026): roles = μόνο διαχειριστής / μόνο προβολή, lockedRoles = owner
 * (φαίνεται, δεν αλλάζει), και «Νέος κωδικός» (onResetPassword) — μόνο όπου ο γονέας το δίνει.
 */

const ROLES = ['org_viewer', 'org_admin', 'org_owner'];

export interface OrgMembersTabProps {
  members: OrgMember[];
  stores: OrgStore[];
  canManage: boolean;
  onUpdateRole: (orgUserId: number, role: string) => Promise<boolean>;
  onSaveScope: (member: OrgMember, storeIds: number[]) => Promise<boolean>;
  onRemove: (orgUserId: number) => Promise<boolean>;
  /** Επιστρέφει μήνυμα σφάλματος (null = πέτυχε). */
  onAdd: (draft: { org_user_id: string; role: string; store_ids: number[] }) => Promise<string | null>;
  /** Νέος χρήστης οργανισμού + μέλος· μήνυμα σφάλματος ή null. Χωρίς αυτό: μόνο «Υπάρχων χρήστης». */
  onCreate?: (draft: NewMemberDraft) => Promise<string | null>;
  /** Ρόλοι που διαλέγονται (προεπιλογή: μόνο προβολή, διαχειριστής, ιδιοκτήτης). */
  roles?: string[];
  /** Μέλη με αυτούς τους ρόλους φαίνονται αλλά δεν αλλάζουν (π.χ. owner στο support). */
  lockedRoles?: string[];
  /** Νέος κωδικός του χρήστη· μήνυμα σφάλματος ή null. Χωρίς αυτό: καμία τέτοια ενότητα. */
  onResetPassword?: (member: OrgMember, password: string) => Promise<string | null>;
}

export interface NewMemberDraft {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  store_ids: number[];
}

export const NEW_MEMBER_MIN_PASSWORD = 8;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const EMPTY_NEW: NewMemberDraft = { first_name: '', last_name: '', email: '', phone: '', password: '', role: 'org_viewer', store_ids: [] };

/** Αρχικός κωδικός 10 χαρακτήρων χωρίς μπερδέματα (0/O, 1/l/I). */
export function generateMemberPassword(length = 10): string {
  const alphabet = 'abcdefghjkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  const bytes = new Uint32Array(length);
  if (typeof window !== 'undefined' && window.crypto?.getRandomValues) window.crypto.getRandomValues(bytes);
  else for (let i = 0; i < length; i++) bytes[i] = Math.floor(Math.random() * 2 ** 32);
  return Array.from(bytes, b => alphabet[b % alphabet.length]).join('');
}

/** Τι λείπει από το νέο μέλος (κλειδί i18n) ή null. */
export function newMemberProblem(d: NewMemberDraft): string | null {
  if (!d.first_name.trim() || !d.last_name.trim() || !d.email.trim() || !d.password) return 'members.newRequired';
  if (!EMAIL_RE.test(d.email.trim())) return 'members.newInvalidEmail';
  if (d.password.length < NEW_MEMBER_MIN_PASSWORD) return 'members.newPasswordShort';
  return null;
}

const sameIds = (a: number[], b: number[]) => a.length === b.length && [...a].sort((x, y) => x - y).every((v, i) => v === [...b].sort((x, y) => x - y)[i]);

/**
 * Επιλογή καταστημάτων ενός μέλους. Κενή επιλογή = ΟΛΑ τα καταστήματα — γράφεται ρητά, ώστε να φαίνεται ότι
 * η προεπιλογή είναι η πλήρης πρόσβαση.
 */
export const StoreScopePicker: React.FC<{
  stores: OrgStore[];
  selected: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
  labelledBy?: string;
}> = ({ stores, selected, onChange, disabled = false, labelledBy }) => {
  const { t } = useTranslation(['orgDashboard']);
  return (
    <div className="org-scope" role="group" aria-labelledby={labelledBy} data-testid="org-scope">
      <label className="org-check org-check-all" data-checked={selected.length === 0 || undefined}>
        <input type="checkbox" checked={selected.length === 0} onChange={() => onChange([])} disabled={disabled} data-testid="org-scope-all" />
        <span>{t('orgDashboard:members.scopeAll')} <span className="text-t4">{t('orgDashboard:members.scopeAllHint')}</span></span>
      </label>
      <div className="org-scope-list">
        {stores.map(s => {
          const checked = selected.includes(s.id);
          return (
            <label key={s.id} className="org-check" data-checked={checked || undefined}>
              <input
                type="checkbox"
                checked={checked}
                disabled={disabled}
                onChange={() => onChange(checked ? selected.filter(id => id !== s.id) : [...selected, s.id])}
                data-testid={`org-scope-${s.id}`}
              />
              <span className="truncate">{storeLabel(s)} <span className="text-t4">#{s.id}</span></span>
            </label>
          );
        })}
      </div>
      {selected.length > 0 && (
        <p className="org-scope-note" data-testid="org-scope-note">{t('orgDashboard:members.scopeOnly', { count: selected.length })}</p>
      )}
    </div>
  );
};

const OrgMembersTab: React.FC<OrgMembersTabProps> = ({
  members, stores, canManage, onUpdateRole, onSaveScope, onRemove, onAdd, onCreate, roles = ROLES, lockedRoles = [], onResetPassword,
}) => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'admin', 'common', 'listPage']);
  const t = tRaw as unknown as TFn;
  const uid = useId();
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [scopeDraft, setScopeDraft] = useState<number[]>([]);
  const [roleSaved, setRoleSaved] = useState(false);
  const [roleBusy, setRoleBusy] = useState(false);
  const [scopeBusy, setScopeBusy] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState<{ org_user_id: string; role: string; store_ids: number[] }>({ org_user_id: '', role: 'org_viewer', store_ids: [] });
  const [addError, setAddError] = useState<string | null>(null);
  const [addBusy, setAddBusy] = useState(false);
  // «Νέος χρήστης» (προεπιλογή όταν ο γονέας δίνει onCreate) ή «Υπάρχων χρήστης» με id.
  const [addMode, setAddMode] = useState<'new' | 'existing'>(onCreate ? 'new' : 'existing');
  const [newDraft, setNewDraft] = useState<NewMemberDraft>(EMPTY_NEW);
  const setNew = (patch: Partial<NewMemberDraft>) => setNewDraft(d => ({ ...d, ...patch }));
  const [resetPw, setResetPw] = useState('');
  const [resetBusy, setResetBusy] = useState(false);
  const [resetNote, setResetNote] = useState<{ ok: boolean; text: string } | null>(null);

  const selected = useMemo(() => members.find(m => m.org_user_id === selectedId) || null, [members, selectedId]);

  // Νέο μέλος στο συρτάρι → το πρόχειρο της πρόσβασης από τα αποθηκευμένα.
  useEffect(() => {
    setScopeDraft(selected?.store_ids || []);
    setRoleSaved(false);
    setResetPw('');
    setResetNote(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- μόνο όταν αλλάζει το μέλος
  }, [selectedId]);

  useEffect(() => {
    if (!roleSaved) return undefined;
    const timer = setTimeout(() => setRoleSaved(false), 2000);
    return () => clearTimeout(timer);
  }, [roleSaved]);

  const rows = useMemo(
    () => members.filter(m => matchesSearch(search, `${m.first_name || ''} ${m.last_name || ''}`, m.email, roleLabel(t, m.role))),
    [members, search, t],
  );

  const scopeText = (m: OrgMember) => (m.store_ids?.length
    ? t('orgDashboard:members.scopeCount', { count: m.store_ids.length })
    : t('orgDashboard:members.scopeAllShort'));
  const scopeNames = (m: OrgMember) => (m.store_ids?.length
    ? stores.filter(s => m.store_ids!.includes(s.id)).map(s => storeLabel(s)).join(', ')
    : t('orgDashboard:members.scopeAllTitle'));

  const columns = useMemo<Column<OrgMember>[]>(() => [
    {
      key: 'name',
      header: t('orgDashboard:members.colMember'),
      width: 'minmax(220px, 2fr)',
      render: m => (
        <>
          <span className="block font-medium truncate">{`${m.first_name || ''} ${m.last_name || ''}`.trim() || `#${m.org_user_id}`}</span>
          {m.email && <span className="block text-xs text-t3 truncate">{m.email}</span>}
        </>
      ),
      stackRender: m => `${m.first_name || ''} ${m.last_name || ''}`.trim() || `#${m.org_user_id}`,
      sortable: true,
      sortValue: m => `${m.first_name || ''} ${m.last_name || ''}`,
    },
    {
      key: 'role',
      header: t('admin:roleSelectLabel'),
      width: 'minmax(130px, 1fr)',
      render: m => <span className="text-t2">{roleLabel(t, m.role)}</span>,
      stackRender: m => roleLabel(t, m.role),
      sortable: true,
      sortValue: m => roleLabel(t, m.role),
    },
    {
      key: 'scope',
      header: t('orgDashboard:members.colScope'),
      width: 'minmax(150px, 1fr)',
      render: m => (m.store_ids?.length
        ? <StatusChip tone="warn" title={scopeNames(m)}>{scopeText(m)}</StatusChip>
        : <span className="text-t3" title={scopeNames(m)}>{scopeText(m)}</span>),
      stackRender: m => scopeText(m),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- scopeText/scopeNames διαβάζουν t/stores
  ], [t, stores]);

  const changeRole = async (role: string) => {
    if (!selected || roleBusy) return;
    setRoleBusy(true);
    setRoleSaved(false);
    const ok = await onUpdateRole(selected.org_user_id, role);
    setRoleBusy(false);
    if (ok) setRoleSaved(true);
  };

  const saveScope = async () => {
    if (!selected || scopeBusy) return;
    setScopeBusy(true);
    const ok = await onSaveScope(selected, scopeDraft);
    setScopeBusy(false);
    if (ok) setSelectedId(null);
  };

  const remove = async () => {
    setConfirmRemove(false);
    if (!selected) return;
    const ok = await onRemove(selected.org_user_id);
    if (ok) setSelectedId(null);
  };

  const closeDetail = () => {
    if (confirmRemove) setConfirmRemove(false);
    else setSelectedId(null);
  };

  const closeAdd = () => {
    setAdding(false);
    setDraft({ org_user_id: '', role: 'org_viewer', store_ids: [] });
    setNewDraft(EMPTY_NEW);
    setAddMode(onCreate ? 'new' : 'existing');
    setAddError(null);
  };

  const creating = addMode === 'new' && !!onCreate;
  const canSubmitAdd = creating ? newMemberProblem(newDraft) === null : !!draft.org_user_id;

  const submitAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    if (addBusy) return;
    if (creating) {
      const problem = newMemberProblem(newDraft);
      if (problem) { setAddError(t(`orgDashboard:${problem}`, { count: NEW_MEMBER_MIN_PASSWORD })); return; }
    } else if (!draft.org_user_id) return;
    setAddBusy(true);
    setAddError(null);
    try {
      const err = creating && onCreate ? await onCreate(newDraft) : await onAdd(draft);
      if (err) setAddError(err);
      else closeAdd();
    } finally {
      setAddBusy(false);
    }
  };

  const scopeDirty = !!selected && !sameIds(scopeDraft, selected.store_ids || []);
  // Ο owner στο support: φαίνεται, δεν αλλάζει (κλιμάκωση σε super admin).
  const editable = !!selected && canManage && !lockedRoles.includes(selected.role);

  const resetPassword = async () => {
    if (!selected || !onResetPassword || resetBusy || resetPw.length < NEW_MEMBER_MIN_PASSWORD) return;
    setResetBusy(true);
    setResetNote(null);
    const err = await onResetPassword(selected, resetPw);
    setResetBusy(false);
    setResetNote(err ? { ok: false, text: err } : { ok: true, text: t('orgDashboard:members.resetDone') });
  };
  const filtered = search.trim() !== '';
  const memberName = (m: OrgMember) => `${m.first_name || ''} ${m.last_name || ''}`.trim() || `#${m.org_user_id}`;

  return (
    <ListPage
      eyebrow={t('orgDashboard:nav.groups.org')}
      title={t('orgDashboard:members.title')}
      subtitle={t('orgDashboard:members.subtitle')}
      testId="org-members"
    >
      <ListToolbar
        search={{
          value: search,
          onChange: setSearch,
          placeholder: t('orgDashboard:members.searchPlaceholder'),
          ariaLabel: t('orgDashboard:members.searchLabel'),
        }}
        actions={canManage ? (
          <button type="button" className={PRIMARY_BTN} onClick={() => setAdding(true)} data-testid="org-member-add">
            <UserPlus className="w-4 h-4" aria-hidden="true" />
            {t('admin:addMember')}
          </button>
        ) : null}
        summary={filtered
          ? t('orgDashboard:members.countFiltered', { shown: rows.length, count: members.length })
          : t('orgDashboard:members.count', { count: members.length })}
      />

      <DataTable
        columns={columns}
        rows={rows}
        rowKey={m => m.org_user_id}
        onRowClick={m => setSelectedId(m.org_user_id)}
        selectedKey={selectedId}
        stickyHeader
        rowLabel={m => memberName(m)}
        rowClassName={m => (m.user_active === false ? 'opacity-70' : undefined)}
        empty={filtered
          ? <EmptyState icon={Users} title={t('orgDashboard:members.noMatch')} />
          : <EmptyState icon={Users} title={t('admin:noMembers')} hint={canManage ? t('orgDashboard:members.emptyHint') : undefined} />}
        testId="org-members-table"
      />

      <DetailPanel
        open={selected !== null}
        onClose={closeDetail}
        title={selected ? memberName(selected) : ''}
        subtitle={selected?.email || undefined}
        testId="org-member-detail"
        footerStart={editable ? (
          <button type="button" className={DANGER_GHOST_BTN} onClick={() => setConfirmRemove(true)} data-testid="org-member-remove">
            {t('admin:removeMemberTitle')}
          </button>
        ) : undefined}
        footer={selected && (editable ? (
          <>
            <button type="button" className={GHOST_BTN} onClick={() => setSelectedId(null)}>{t('listPage:close')}</button>
            <button type="button" className={PRIMARY_BTN} onClick={saveScope} disabled={!scopeDirty || scopeBusy} data-testid="org-member-scope-save">
              {scopeBusy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {t('orgDashboard:members.saveScope')}
            </button>
          </>
        ) : (
          <button type="button" className={GHOST_BTN} onClick={() => setSelectedId(null)}>{t('listPage:close')}</button>
        ))}
      >
        {selected && (editable ? (
          <div className="space-y-6">
            <SheetField
              label={t('admin:roleSelectLabel')}
              htmlFor={`${uid}-role`}
              hint={roleSaved
                ? <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400" role="status"><Check className="w-3.5 h-3.5" aria-hidden="true" />{t('orgDashboard:members.roleSaved')}</span>
                : t('orgDashboard:members.roleHint')}
            >
              <select
                id={`${uid}-role`}
                value={selected.role}
                onChange={e => changeRole(e.target.value)}
                disabled={roleBusy}
                className={SELECT_CLS}
                data-testid="org-member-role"
              >
                {/* Ρόλος εκτός των τριών (π.χ. org_editor): φαίνεται όπως είναι, δεν διαλέγεται για άλλους. */}
                {!roles.includes(selected.role) && <option value={selected.role}>{roleLabel(t, selected.role)}</option>}
                {roles.map(r => <option key={r} value={r}>{roleLabel(t, r)}</option>)}
              </select>
            </SheetField>
            <div>
              <p id={`${uid}-scope`} className="xs-label">{t('orgDashboard:members.scopeLabel')}</p>
              <StoreScopePicker stores={stores} selected={scopeDraft} onChange={setScopeDraft} disabled={scopeBusy} labelledBy={`${uid}-scope`} />
            </div>
            {onResetPassword && (
              <SheetField label={t('orgDashboard:members.resetTitle')} htmlFor={`${uid}-reset`}
                hint={t('orgDashboard:members.resetHint', { count: NEW_MEMBER_MIN_PASSWORD })}>
                <div className="flex flex-wrap gap-2">
                  {/* Ορατός: τον δίνει το support στο μέλος. */}
                  <input id={`${uid}-reset`} type="text" value={resetPw} onChange={e => { setResetPw(e.target.value); setResetNote(null); }}
                    className={`${INPUT_CLS} font-mono flex-1 min-w-0`} autoComplete="new-password" spellCheck={false} data-testid="org-member-reset-input" />
                  <button type="button" className={GHOST_BTN} onClick={() => { setResetPw(generateMemberPassword()); setResetNote(null); }}
                    data-testid="org-member-reset-generate">{t('orgDashboard:members.generatePassword')}</button>
                  <button type="button" className={GHOST_BTN} onClick={resetPassword}
                    disabled={resetBusy || resetPw.length < NEW_MEMBER_MIN_PASSWORD} data-testid="org-member-reset-save">
                    {resetBusy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
                    {t('orgDashboard:members.resetButton')}
                  </button>
                </div>
                {resetNote && (
                  <p role="status" data-testid="org-member-reset-note"
                    className={`mt-1.5 text-xs ${resetNote.ok ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-700 dark:text-red-300'}`}>{resetNote.text}</p>
                )}
              </SheetField>
            )}
          </div>
        ) : (
          <>
          {selected && canManage && lockedRoles.includes(selected.role) && (
            <div className="mb-4"><Notice testId="org-member-locked">{t('orgDashboard:members.lockedOwner')}</Notice></div>
          )}
          <KeyValue
            testId="org-member-values"
            items={[
              { id: 'role', label: t('admin:roleSelectLabel'), value: roleLabel(t, selected.role) },
              { id: 'email', label: t('common:email'), value: selected.email || null },
              { id: 'phone', label: t('common:phone'), value: selected.phone || null },
              { id: 'scope', label: t('orgDashboard:members.scopeLabel'), value: scopeNames(selected) },
            ]}
          />
          </>
        ))}
      </DetailPanel>

      <DetailPanel
        open={adding}
        onClose={closeAdd}
        title={t('admin:addMemberTitle')}
        subtitle={t('orgDashboard:members.addSubtitle')}
        testId="org-member-add-panel"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeAdd}>{t('orgDashboard:actions.cancel')}</button>
            <button type="submit" form={`${uid}-add`} className={PRIMARY_BTN} disabled={!canSubmitAdd || addBusy} data-testid="org-member-add-submit">
              {addBusy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {creating ? t('orgDashboard:members.createButton') : t('admin:addButton')}
            </button>
          </>
        )}
      >
        <form id={`${uid}-add`} onSubmit={submitAdd} noValidate className="space-y-5">
          {onCreate && (
            <div role="group" aria-label={t('orgDashboard:members.addModeLabel')} className="grid grid-cols-2 gap-1 p-1 rounded-xl bg-surface-1 border border-line">
              {(['new', 'existing'] as const).map(mode => (
                <button key={mode} type="button" aria-pressed={addMode === mode} data-testid={`org-member-mode-${mode}`}
                  onClick={() => { setAddMode(mode); setAddError(null); }}
                  className={`h-10 rounded-lg text-sm font-semibold transition-colors ${addMode === mode ? 'bg-surface-2 text-t1 border border-line shadow-sm' : 'text-t3 hover:text-t1'}`}>
                  {t(mode === 'new' ? 'orgDashboard:members.modeNew' : 'orgDashboard:members.modeExisting')}
                </button>
              ))}
            </div>
          )}

          {creating ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <SheetField label={t('orgDashboard:members.firstName')} htmlFor={`${uid}-fn`}>
                  <input id={`${uid}-fn`} value={newDraft.first_name} onChange={e => setNew({ first_name: e.target.value })}
                    className={INPUT_CLS} autoFocus autoComplete="off" data-testid="org-member-new-first" />
                </SheetField>
                <SheetField label={t('orgDashboard:members.lastName')} htmlFor={`${uid}-ln`}>
                  <input id={`${uid}-ln`} value={newDraft.last_name} onChange={e => setNew({ last_name: e.target.value })}
                    className={INPUT_CLS} autoComplete="off" data-testid="org-member-new-last" />
                </SheetField>
              </div>
              <SheetField label={t('common:email')} htmlFor={`${uid}-em`} hint={t('orgDashboard:members.emailHint')}>
                <input id={`${uid}-em`} type="email" value={newDraft.email} onChange={e => setNew({ email: e.target.value })}
                  className={INPUT_CLS} autoComplete="off" data-testid="org-member-new-email" />
              </SheetField>
              <SheetField label={t('orgDashboard:members.phoneOptional')} htmlFor={`${uid}-ph`}>
                <input id={`${uid}-ph`} type="tel" value={newDraft.phone} onChange={e => setNew({ phone: e.target.value })}
                  className={INPUT_CLS} autoComplete="off" data-testid="org-member-new-phone" />
              </SheetField>
              <SheetField label={t('orgDashboard:members.password')} htmlFor={`${uid}-pw`}
                hint={t('orgDashboard:members.passwordHint', { count: NEW_MEMBER_MIN_PASSWORD })}>
                <div className="flex gap-2">
                  {/* Ορατός: τον δίνει ο διαχειριστής στο μέλος μαζί με το email. */}
                  <input id={`${uid}-pw`} type="text" value={newDraft.password} onChange={e => setNew({ password: e.target.value })}
                    className={`${INPUT_CLS} font-mono`} autoComplete="new-password" spellCheck={false} data-testid="org-member-new-password" />
                  <button type="button" className={GHOST_BTN} onClick={() => setNew({ password: generateMemberPassword() })} data-testid="org-member-new-generate">
                    {t('orgDashboard:members.generatePassword')}
                  </button>
                </div>
              </SheetField>
              <SheetField label={t('admin:roleSelectLabel')} htmlFor={`${uid}-nrole`}>
                <select id={`${uid}-nrole`} value={newDraft.role} onChange={e => setNew({ role: e.target.value })} className={SELECT_CLS} data-testid="org-member-add-role">
                  {roles.map(r => <option key={r} value={r}>{roleLabel(t, r)}</option>)}
                </select>
              </SheetField>
              <div>
                <p id={`${uid}-nscope`} className="xs-label">{t('orgDashboard:members.scopeLabel')}</p>
                <StoreScopePicker stores={stores} selected={newDraft.store_ids} onChange={ids => setNew({ store_ids: ids })} labelledBy={`${uid}-nscope`} />
              </div>
            </>
          ) : (
            <>
              <SheetField label={t('admin:orgUserIdLabel')} htmlFor={`${uid}-uid`} hint={t('orgDashboard:members.orgUserIdHint')}>
                <input
                  id={`${uid}-uid`}
                  type="number"
                  value={draft.org_user_id}
                  onChange={e => setDraft(d => ({ ...d, org_user_id: e.target.value }))}
                  placeholder={t('admin:orgUserIdPlaceholder')}
                  className={INPUT_CLS}
                  autoFocus
                  data-testid="org-member-add-id"
                />
              </SheetField>
              <SheetField label={t('admin:roleSelectLabel')} htmlFor={`${uid}-nrole`}>
                <select id={`${uid}-nrole`} value={draft.role} onChange={e => setDraft(d => ({ ...d, role: e.target.value }))} className={SELECT_CLS} data-testid="org-member-add-role">
                  {roles.map(r => <option key={r} value={r}>{roleLabel(t, r)}</option>)}
                </select>
              </SheetField>
              <div>
                <p id={`${uid}-nscope`} className="xs-label">{t('orgDashboard:members.scopeLabel')}</p>
                <StoreScopePicker stores={stores} selected={draft.store_ids} onChange={ids => setDraft(d => ({ ...d, store_ids: ids }))} labelledBy={`${uid}-nscope`} />
              </div>
            </>
          )}
          {addError && <Notice tone="danger" testId="org-member-add-error">{addError}</Notice>}
        </form>
      </DetailPanel>

      <OrgConfirm
        isOpen={confirmRemove && selected !== null}
        title={t('admin:removeMemberTitle')}
        message={selected ? `${memberName(selected)} — ${t('admin:confirmRemoveMember')}` : ''}
        confirmLabel={t('orgDashboard:members.removeConfirm')}
        cancelLabel={t('orgDashboard:actions.cancel')}
        variant="danger"
        onConfirm={remove}
        onCancel={() => setConfirmRemove(false)}
      />
    </ListPage>
  );
};

export default OrgMembersTab;
